/**
 * Palantir PMS - Admin Panel - User Management
 * Boton para refrescar permisos del admin
 */

import React, { useEffect, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import "./UserManagement.css";

function UserManagement() {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const updateAdminPerms = async () => {
    if (!window.confirm("Seguro que quieres actualizar los permisos del admin?")) return;
    setLoading(true);
    setMsg("");

    try {
      const res = await axios.put("/api/roles/1", {
        name: "admin",
        description: "Super usuario con todos los permisos",
        permissions: {},
      });
      console.log("[ADMIN][PermsUpdate] OK:", res.data);
      setMsg("Admin permissions updated successfully.");
    } catch (err) {
      console.error("[ADMIN][PermsUpdate][ERR]", err);
      setMsg("Error updating admin permissions.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ color: "#fff" }}>User Management</h2>

      <button
        onClick={updateAdminPerms}
        disabled={loading}
        style={{
          marginTop: "16px",
          padding: "10px 18px",
          backgroundColor: "#5f2c82",
          color: "#fff",
          border: "none",
          borderRadius: "8px",
          fontWeight: 600,
          cursor: loading ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "Updating..." : "Update Admin Permits"}
      </button>

      {msg && (
        <div
          style={{
            marginTop: "12px",
            color: msg.toLowerCase().includes("error") ? "#e74c3c" : "#2ecc71",
            fontWeight: "bold",
          }}
        >
          {msg}
        </div>
      )}

      <UserModulesManager />
    </div>
  );
}

const CORPORATE_MODULE = "corporate_purchasing";
const MODULE_OPTIONS = [
  { key: "pms", label: "PMS", managedByRole: true },
  { key: "purchasing", label: "Purchasing", managedByRole: true },
  { key: "pos", label: "POS" },
  { key: "analytics", label: "Management / Cost control" },
  { key: "onboarding", label: "Onboarding" },
  { key: "crm", label: "CRM", managedByRole: true },
];
const localModules = (modules) => Array.isArray(modules)
  ? modules.filter((key) => key !== CORPORATE_MODULE)
  : [];
const mergeEffectiveModules = (roleModules, configuredModules) => {
  const enabled = new Set([...localModules(roleModules), ...localModules(configuredModules)]);
  return MODULE_OPTIONS.map((option) => option.key).filter((key) => enabled.has(key));
};

function UserModulesManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [savingUsers, setSavingUsers] = useState({});
  const [userErrors, setUserErrors] = useState({});
  const usersRef = useRef([]);
  const pendingUsers = useRef(new Set());
  const mounted = useRef(true);
  const canManageAccess = Number(readStoredUser()?.id) === 1;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    axios.get("/api/users")
      .then(({ data }) => {
        if (cancelled) return;
        usersRef.current = Array.isArray(data) ? data : [];
        setUsers(usersRef.current);
      })
      .catch(() => {
        if (!cancelled) setLoadError("Could not load users. Please try again.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reload]);

  const updateUser = (userId, update) => {
    usersRef.current = usersRef.current.map((user) => Number(user.id) === Number(userId) ? update(user) : user);
    setUsers(usersRef.current);
  };

  const saveAccess = async (userId, field, value, optimisticValue) => {
    const key = String(userId);
    if (Number(readStoredUser()?.id) !== 1 || pendingUsers.current.has(key)) return;
    const previousUser = usersRef.current.find((user) => Number(user.id) === Number(userId));
    if (!previousUser) return;

    pendingUsers.current.add(key);
    setSavingUsers((current) => ({ ...current, [key]: true }));
    setUserErrors((current) => ({ ...current, [key]: "" }));
    updateUser(userId, (user) => ({ ...user, [field]: optimisticValue }));

    try {
      const response = await axios.patch("/api/users/" + userId, { [field]: value });
      const savedValue = response.data?.user?.[field];
      if (mounted.current && Array.isArray(savedValue)) {
        // Corporate and BU permissions are separate; never replace the other workspace's grants.
        updateUser(userId, (user) => ({ ...user, [field]: savedValue }));
      }
    } catch (_error) {
      if (mounted.current) {
        updateUser(userId, (user) => ({ ...user, [field]: previousUser[field] }));
        setUserErrors((current) => ({
          ...current,
          [key]: "Could not save access. Previous permissions were restored. Please try again.",
        }));
      }
    } finally {
      pendingUsers.current.delete(key);
      if (mounted.current) setSavingUsers((current) => ({ ...current, [key]: false }));
    }
  };

  const handleChangeModules = (userId, businessUnitId, configuredModules) => {
    const targetUser = usersRef.current.find((user) => Number(user.id) === Number(userId));
    if (!targetUser) return;
    const configured = localModules(configuredModules);
    const updatedAssignments = (targetUser.role_assignments || []).map((assignment) => {
      const payload = { business_unit_id: assignment.business_unit_id, role_id: assignment.role_id };
      if (Number(assignment.business_unit_id) === Number(businessUnitId)) {
        payload.configured_allowed_modules = configured;
      } else if (Array.isArray(assignment.configured_allowed_modules)) {
        payload.configured_allowed_modules = localModules(assignment.configured_allowed_modules);
      }
      return payload;
    }).filter((assignment) => Number.isFinite(Number(assignment.business_unit_id)) && Number.isFinite(Number(assignment.role_id)));
    const optimisticAssignments = (targetUser.role_assignments || []).map((assignment) => (
      Number(assignment.business_unit_id) === Number(businessUnitId) ? {
        ...assignment,
        configured_allowed_modules: configured,
        allowed_modules: mergeEffectiveModules(assignment.role_allowed_modules, configured),
      } : assignment
    ));
    void saveAccess(userId, "role_assignments", updatedAssignments, optimisticAssignments);
  };

  const handleCorporateChange = (user) => {
    if (Number(user.id) === 1 || user.role_derived_corporate_modules?.includes(CORPORATE_MODULE)) return;
    const enabled = Array.isArray(user.corporate_modules) && user.corporate_modules.includes(CORPORATE_MODULE);
    const modules = enabled ? [] : [CORPORATE_MODULE];
    void saveAccess(user.id, "corporate_modules", modules, modules);
  };

  return (
    <section className="userAccessPanel" aria-labelledby="user-access-heading">
      <header className="userAccessPanel__header">
        <h3 id="user-access-heading">Workspace access</h3>
        <p>Business unit modules and corporate workspaces have independent access.</p>
        <p>PMS, Purchasing and CRM are enabled by the assigned role. Edit its permissions in Settings to change them.</p>
      </header>
      {!canManageAccess && <p className="userAccessPanel__notice">Only the main administrator can change workspace access.</p>}
      {loading ? <p className="userAccessPanel__notice" role="status">Loading users...</p> : loadError ? (
        <div className="userAccessPanel__error" role="alert">
          <span>{loadError}</span><button type="button" onClick={() => setReload((current) => current + 1)}>Try again</button>
        </div>
      ) : users.length === 0 ? <p className="userAccessPanel__notice">No users available.</p> : (
        <div className="userAccessPanel__users">
          {users.map((user) => {
            const assignments = [...(user.role_assignments || [])].sort((a, b) => String(a?.business_unit?.name || a?.business_unit_id || "")
              .localeCompare(String(b?.business_unit?.name || b?.business_unit_id || "")));
            const isMaster = Number(user.id) === 1;
            const corporateByRole = user.role_derived_corporate_modules?.includes(CORPORATE_MODULE) === true;
            const corporateEnabled = isMaster || corporateByRole || (Array.isArray(user.corporate_modules) && user.corporate_modules.includes(CORPORATE_MODULE));
            const saving = Boolean(savingUsers[String(user.id)]);
            const userName = user.name || user.email || "(no name)";

            return (
              <article className="userAccessPanel__user" key={user.id} data-user-id={user.id} aria-busy={saving}>
                <header className="userAccessPanel__identity">
                  <h4>{userName}</h4>
                  <p>{user.email}</p>
                </header>
                <div className="userAccessPanel__workspaces">
                  <div className="userAccessPanel__business-units">
                    {assignments.length === 0 ? <p className="userAccessPanel__empty">No business units assigned.</p> : assignments.map((assignment) => {
                      const modules = localModules(assignment.allowed_modules);
                      const roleModules = localModules(assignment.role_allowed_modules);
                      const configured = Array.isArray(assignment.configured_allowed_modules)
                        ? localModules(assignment.configured_allowed_modules)
                        : modules.filter((key) => !roleModules.includes(key));
                      return (
                        <div className="userAccessPanel__workspace" key={assignment.business_unit_id} data-business-unit-id={assignment.business_unit_id}>
                          <div className="userAccessPanel__workspace-label">
                            <h5>{assignment.business_unit?.name || "BU " + assignment.business_unit_id}</h5>
                            <p>{assignment.role?.name || "Role " + assignment.role_id}</p>
                          </div>
                          <div className="userAccessPanel__modules">
                            {MODULE_OPTIONS.map((option) => {
                              const active = modules.includes(option.key);
                              return (
                                <button
                                  key={option.key} type="button" aria-pressed={active}
                                  disabled={Boolean(option.managedByRole) || saving || !canManageAccess}
                                  title={option.managedByRole ? "This module is controlled by the permissions of the assigned role." : "Manually " + (active ? "disable" : "enable") + " " + option.label}
                                  onClick={() => handleChangeModules(user.id, assignment.business_unit_id, active ? configured.filter((key) => key !== option.key) : [...configured, option.key])}
                                >
                                  {option.managedByRole && active ? "Role" : active ? "Yes" : "No"} {option.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="userAccessPanel__workspace userAccessPanel__corporate" data-workspace-kind="corporate">
                    <div className="userAccessPanel__workspace-label">
                      <h5>Compras corporativas / Corporate Purchasing</h5>
                      <p>Hotel access follows Users & Roles assignments</p>
                      <p>Shared catalog, suppliers, tenders and common units.</p>
                      {corporateByRole && <p>Enabled by the assigned role. Change its corporate purchasing permission to remove this access.</p>}
                    </div>
                    <button
                      type="button" role="switch" className="userAccessPanel__corporate-toggle"
                      aria-label={"Corporate Purchasing access for " + userName} aria-checked={corporateEnabled}
                      disabled={isMaster || corporateByRole || saving || !canManageAccess}
                      title={isMaster ? "The main administrator always has corporate access." : corporateByRole ? "Corporate purchasing is enabled by the assigned role." : "Enable or disable this corporate workspace within assigned hotels."}
                      onClick={() => handleCorporateChange(user)}
                    >
                      <span className="userAccessPanel__switch-track" aria-hidden="true"><span /></span>
                      <span>{isMaster ? "Always active · Main administrator" : corporateEnabled ? "Enabled" : "Disabled"}</span>
                    </button>
                  </div>
                  {saving && <p className="userAccessPanel__notice" role="status">Saving access…</p>}
                  {userErrors[String(user.id)] && <p className="userAccessPanel__error" role="alert">{userErrors[String(user.id)]}</p>}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default UserManagement;
